import api from './api';
import type { Gallery, GalleryFormData, GalleryStatistics, ApiResponse } from '../../types/gallery.types';

class GalleryService {
  private readonly endpoint = '/api/gallery';

  async getAll(category?: string): Promise<ApiResponse<Gallery[]>> {
    const params = category ? { category } : {};
    const response = await api.get<ApiResponse<Gallery[]>>(this.endpoint, { params });
    return response.data;
  }

  async getByCategory(category: string): Promise<ApiResponse<Gallery[]>> {
    const response = await api.get<ApiResponse<Gallery[]>>(`${this.endpoint}/category/${category}`);
    return response.data;
  }

  async getCategories(): Promise<ApiResponse<string[]>> {
    const response = await api.get<ApiResponse<string[]>>(`${this.endpoint}/categories/list`);
    return response.data;
  }

  async getStatistics(): Promise<ApiResponse<GalleryStatistics>> {
    const response = await api.get<ApiResponse<GalleryStatistics>>(`${this.endpoint}/statistics`);
    return response.data;
  }

  async search(searchTerm: string): Promise<ApiResponse<Gallery[]>> {
    const response = await api.get<ApiResponse<Gallery[]>>(`${this.endpoint}/search`, {
      params: { q: searchTerm },
    });
    return response.data;
  }

  async getById(id: number): Promise<ApiResponse<Gallery>> {
    const response = await api.get<ApiResponse<Gallery>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async uploadImage(file: File): Promise<ApiResponse<{ url: string; filename: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post<ApiResponse<{ url: string; filename: string }>>(
      `${this.endpoint}/upload-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async create(imageData: GalleryFormData): Promise<ApiResponse<Gallery>> {
    const response = await api.post<ApiResponse<Gallery>>(this.endpoint, imageData);
    return response.data;
  }

  async update(id: number, imageData: Partial<GalleryFormData>): Promise<ApiResponse<Gallery>> {
    const response = await api.put<ApiResponse<Gallery>>(`${this.endpoint}/${id}`, imageData);
    return response.data;
  }

  async reorder(imageIds: number[]): Promise<ApiResponse<void>> {
    const response = await api.put<ApiResponse<void>>(`${this.endpoint}/reorder/batch`, { imageIds });
    return response.data;
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async toggleStatus(id: number): Promise<ApiResponse<Gallery>> {
    const response = await api.put<ApiResponse<Gallery>>(`${this.endpoint}/${id}/toggle-status`);
    return response.data;
  }
}

export const galleryService = new GalleryService();
